import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { UserManagementEntityType, UserManagementState } from '../../Utils/types';
import { isValidEntity, APP_NAME, BASE_URL } from '../../Utils/utils';
import { useUserManagementContext } from '../UserManagementContext';
import { UserManagementService } from '../Services/UserManagementService';
import { useUserManagementStore } from '../Store/useUserManagementStore';
import { logger, useBIEvents } from '@common-ui-library/shared';
import { getListViewEvent, getDetailViewEvent, getEntityIdKey } from '../../Utils/biEvents';

export const useUserManagementData = () => {
  const { userManagementEntity, entityId } = useParams<{
    userManagementEntity: UserManagementEntityType;
    entityId: string;
  }>();
  const { service } = useUserManagementContext();
  const navigate = useNavigate();
  const { sendBIEvent } = useBIEvents();

  const { 
    setState, 
    fetchEntities, 
    setQueryState, 
    getDropdownOptions,
    setDropdownOptions,
    resetStore
  } = useUserManagementStore(
    useShallow((state) => ({
      setState: state.setState,
      fetchEntities: state.fetchEntities,
      setQueryState: state.setQueryState,
      getDropdownOptions: state.getDropdownOptions,
      setDropdownOptions: state.setDropdownOptions,
      resetStore: state.resetStore,
    }))
  );


  // Update Entity Type in Store
  useEffect(() => {
    if (userManagementEntity && isValidEntity(userManagementEntity)) {
      setState({ userManagementEntity });
    }
  }, [userManagementEntity, setState]);

  // Fetch Initial List Data
  useEffect(() => {
    if (!userManagementEntity || !isValidEntity(userManagementEntity)) return;

    const fetchData = async () => {
      // Reset Store for new entity type
      setQueryState({ search: null, filters: null, ordering: null });
      
      // Fetch List
      const result = await fetchEntities(service, userManagementEntity, { page: 1 });
      
      // Emit list view BI event
      if (result?.data?.pagination) {
        sendBIEvent(getListViewEvent(userManagementEntity), {
          path: window.location.pathname,
          count: {total: result.data.pagination.total_count}
        });
      }
      
      // Fetch Metadata (Dropdowns)
      await getDropdownOptions(service, userManagementEntity);
    };

    fetchData();

    return () => {
      // Optional: Clear store on unmount or entity change if desired
      // resetStore(); 
      // Keeping it might be better for UX if navigating back, but let's stick to current behavior
      setQueryState({ search: null, filters: null, ordering: null });
      setState({ items: [], pagination: undefined });
      setDropdownOptions({});
    };
  }, [userManagementEntity, service, fetchEntities, setQueryState, setState, getDropdownOptions, setDropdownOptions, sendBIEvent]);

  // Fetch Single Entity by ID (Deep Link)
  useEffect(() => {
    const fetchEntityById = async () => {
      if (!entityId || !userManagementEntity) {
        return;
      }

      try {
        logger.info(`Fetching ${userManagementEntity} entity by id: ${entityId}`);
        const { status, data } = await service.fetchEntities(
          userManagementEntity,
          { entityId }
        );
        
        if (data?.id) {
          logger.info(`Successfully fetched ${userManagementEntity} entity: ${entityId}`);
          setState({ selectedItem: data });
          
          // Emit detail view BI event
          sendBIEvent(getDetailViewEvent(userManagementEntity), {
            field_name: getEntityIdKey(userManagementEntity),
            field_content: entityId,
            path: window.location.pathname,
          });
        } else {
          // Entity not found - redirect to list
          logger.error(`Entity not found: ${userManagementEntity}/${entityId}`);
          setState({ selectedItem: null });
          navigate(`${BASE_URL}${userManagementEntity}/`, { replace: true });
        }

      } catch (error: any) {
        logger.error(`Error fetching entity by id: ${entityId}`, error);
        setState({ selectedItem: null });
        // Redirect to list on any error
        navigate(`${BASE_URL}${userManagementEntity}/`, { replace: true });
      }
    };

    fetchEntityById();
  }, [entityId, userManagementEntity, service, setState, navigate, sendBIEvent]);

  return {
    isValidEntityStatus: isValidEntity(userManagementEntity || ''),
    service
  };
};

